import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Sparkles, ShieldCheck, Zap, LineChart } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

function useCountUp(target, duration = 1800, start = 0) {
  const [val, setVal] = useState(start);
  useEffect(() => {
    let raf = 0;
    let t0 = 0;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(start + (target - start) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

function Nav() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050505]/50 border-b border-white/[0.06]"
      data-testid="obsidian-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 h-16 flex items-center justify-between">
        <Link to="/designs" className="font-mono text-[11px] tracking-[0.3em] uppercase flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#F2F0EB]" /> Obsidian
        </Link>
        <div className="hidden md:flex items-center gap-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
          <a href="#core">Core</a>
          <a href="#rates">Rates</a>
          <a href="#vault">Vault</a>
        </div>
        <Link
          to="/designs"
          className="font-mono text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full border border-white/10 hover:bg-white/5"
        >
          ← Variants
        </Link>
      </div>
    </header>
  );
}

const ratesData = [
  { sym: "USD/T+0", v: "0.04%", d: "+0.002" },
  { sym: "EUR-TRSY", v: "4.21%", d: "+0.014" },
  { sym: "GBP-CR-A", v: "5.86%", d: "-0.008" },
  { sym: "JPY-VLT", v: "0.61%", d: "+0.004" },
  { sym: "CHF-RES", v: "1.92%", d: "+0.001" },
  { sym: "SGD-CL", v: "3.74%", d: "-0.012" },
  { sym: "USD-PCRD", v: "8.92%", d: "+0.041" },
  { sym: "EUR-STR-B", v: "6.08%", d: "+0.022" },
];

const cards = [
  { icon: Zap, t: "Velocity", d: "T+0 atomic clearing across all asset rails." },
  { icon: ShieldCheck, t: "Sovereignty", d: "Bankruptcy-remote, two-trustee custody." },
  { icon: LineChart, t: "Yield Strata", d: "Tiered allocation, programmable triggers." },
  { icon: Sparkles, t: "Concierge", d: "Human origination desk, 24 / 7 / 365." },
];

export default function Obsidian() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.4]);

  const aum = useCountUp(4.2, 2400);
  const bps = useCountUp(212, 2200);
  const j = useCountUp(37, 1800);

  return (
    <main className="min-h-screen bg-[#050505] text-[#F2F0EB] grain overflow-x-hidden" data-testid="obsidian-page">
      <Nav />

      {/* HERO */}
      <section ref={ref} className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        {/* Floating obsidian core */}
        <motion.div
          style={{ rotate, scale }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vmin] h-[80vmin] pointer-events-none"
        >
          <img
            src="https://images.unsplash.com/photo-1634382851346-890ee66f08b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxibGFjayUyMHN0b25lJTIwbW9ub2xpdGh8ZW58MHx8fGJsYWNrfDE3NzcyMTczMjd8MA&ixlib=rb-4.1.0&q=85"
            alt="core"
            className="w-full h-full object-contain mix-blend-screen opacity-90"
            draggable="false"
          />
        </motion.div>

        {/* halo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 40% at 50% 50%, rgba(242,240,235,0.07), rgba(0,0,0,0) 70%)",
          }}
        />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 w-full pt-24">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease, delay: 0.3 }}
            className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-8"
          >
            <span className="w-8 h-[1px] bg-[#404040]" />
            <span>Live · Tier-1 · Encrypted</span>
          </motion.div>

          <motion.h1
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.4 }}
            className="font-display uppercase leading-[0.82] tracking-tight text-[12vw] lg:text-[8.5vw] max-w-[14ch]"
          >
            A glass desk
            <br />
            <span className="text-[#F2F0EB]/55">around an</span>
            <br />
            obsidian core.
          </motion.h1>

          {/* Glass metric bar */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.1, ease, delay: 0.7 }}
            className="mt-14 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-5 sm:p-6 grid grid-cols-3 divide-x divide-white/10"
          >
            <div className="px-4">
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]">AUM Cleared</div>
              <div className="mt-1 font-display text-3xl">${aum.toFixed(1)}B</div>
            </div>
            <div className="px-4">
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]">Avg Yield</div>
              <div className="mt-1 font-display text-3xl">{Math.round(bps)} bps</div>
            </div>
            <div className="px-4">
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]">Jurisdictions</div>
              <div className="mt-1 font-display text-3xl">{Math.round(j)}</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease, delay: 0.9 }}
            className="mt-10 flex items-center gap-3"
          >
            <button className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-full bg-[#F2F0EB] text-[#050505]">
              Open the Desk
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-6 py-3.5 rounded-full border border-white/10 backdrop-blur bg-white/[0.02] hover:bg-white/[0.06]">
              See live rates
            </button>
          </motion.div>
        </div>
      </section>

      {/* RATES TICKER */}
      <section id="rates" className="border-y border-white/[0.06] bg-black/40 backdrop-blur py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...ratesData, ...ratesData].map((r, i) => (
            <div key={i} className="flex items-center gap-3 mr-10 font-mono text-xs tracking-[0.2em]">
              <span className="text-[#8C8C8C]">{r.sym}</span>
              <span className="text-[#F2F0EB]">{r.v}</span>
              <span className={r.d.startsWith("-") ? "text-[#9a9a9a]" : "text-[#F2F0EB]/85"}>{r.d}</span>
              <span className="w-1 h-1 rounded-full bg-[#404040]" />
            </div>
          ))}
        </div>
      </section>

      {/* CORE — glass cards over rock */}
      <section id="core" className="relative py-32 sm:py-40 overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1712730324756-e4879e9c9b11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZ3JleSUyMHNsYXRlJTIwc3RvbmUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NzIxNzMzOXww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover grayscale"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,5,5,1) 0%, rgba(5,5,5,0.4) 50%, rgba(5,5,5,1) 100%)",
            }}
          />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] uppercase text-[#8C8C8C] mb-6">
              <span className="w-8 h-[1px] bg-[#404040]" />
              <span>The Core</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Four primitives.
              <br />
              <span className="text-[#F2F0EB]/55">One operating layer.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.t}
                  initial={{ y: 40, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.9, ease, delay: i * 0.08 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-7 group hover:bg-white/[0.045] transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl border border-white/10 flex items-center justify-center mb-8 group-hover:border-white/30 transition-colors">
                    <Icon className="w-4 h-4 text-[#F2F0EB]" />
                  </div>
                  <div className="font-display uppercase text-2xl">{c.t}</div>
                  <p className="mt-3 font-body text-sm text-[#8C8C8C] leading-relaxed">{c.d}</p>
                  <div className="mt-8 pt-5 border-t border-white/5 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C] flex justify-between">
                    <span>0{i + 1} / 04</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VAULT CTA */}
      <section id="vault" className="relative py-32 sm:py-40 border-t border-white/[0.06] text-center">
        <div className="max-w-[1100px] mx-auto px-6">
          <h2 className="font-display uppercase text-5xl sm:text-7xl leading-[0.95]">
            The vault is open.
            <br />
            <span className="text-[#F2F0EB]/55 italic">Briefly.</span>
          </h2>
          <p className="mt-8 font-body text-[#8C8C8C] max-w-xl mx-auto">
            Access is reviewed by our origination desk within seventy-two hours.
            We onboard a small cohort each quarter, by intent.
          </p>
          <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-xl pl-5 pr-2 py-2">
            <input
              placeholder="you@institution.com"
              className="bg-transparent outline-none placeholder:text-[#5a5a5a] text-[#F2F0EB] font-mono text-sm w-72"
            />
            <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-2.5 rounded-full bg-[#F2F0EB] text-[#050505]">
              Reserve
            </button>
          </div>
          <div className="mt-16 font-mono text-[10px] tracking-[0.3em] uppercase text-[#5a5a5a]">
            Credit Stone Holdings AG · Zürich · Singapore · MMXXVI
          </div>
        </div>
      </section>
    </main>
  );
}
